import { useEffect, useId, useRef, useState } from 'react';
import { AnchoredPanel } from './Anchored.js';
import { DatePicker } from './DatePicker.js';
import { IconButton } from './IconButton.js';
import { BottomSheet } from './Overlay.js';
import type { FieldFrameProps } from './TextField.js';
import { iconEvent } from './generated/icons.js';
import { SIDE_SHEET_QUERY, cx, useMediaQuery } from './util.js';

const pad = (n: number) => String(n).padStart(2, '0');

/** `2026-11-01` shown as `01/11/2026`. An empty or malformed value shows nothing. */
export function formatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m === null ? '' : `${m[3] ?? ''}/${m[2] ?? ''}/${m[1] ?? ''}`;
}

/** What a person types (`1/11/2026`, `01-11-2026`, `1.11.2026`) as an ISO date, or null if it is not a real day. */
export function parseDate(text: string): string | null {
  const m = /^\s*(\d{1,2})[/.\- ](\d{1,2})[/.\- ](\d{4})\s*$/.exec(text);
  if (m === null) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  const real = new Date(Date.UTC(year, month - 1, day));
  if (
    real.getUTCFullYear() !== year ||
    real.getUTCMonth() !== month - 1 ||
    real.getUTCDate() !== day
  )
    return null;
  return `${String(year)}-${pad(month)}-${pad(day)}`;
}

export interface DateFieldProps extends FieldFrameProps {
  /** ISO date `yyyy-mm-dd`, or an empty string for none. */
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  locale?: string;
  placeholder?: string;
  /** Said under the field when what was typed is not a date, or is outside min and max. */
  invalidText?: string;
  openLabel?: string;
  closeLabel?: string;
  previousMonth?: string;
  nextMonth?: string;
}

/**
 * A date you can type (`dd/mm/yyyy`) or pick. The picker is the design system's month grid: a
 * bottom sheet on a phone, a panel under the field on a wide window. Dates stay ISO strings.
 */
export function DateField({
  label,
  supporting,
  error,
  className,
  value,
  onChange,
  min,
  max,
  required = false,
  disabled = false,
  id,
  name,
  locale = 'en-IN',
  placeholder = 'dd/mm/yyyy',
  invalidText = 'Use the form dd/mm/yyyy.',
  openLabel = 'Choose a date',
  closeLabel = 'Close',
  previousMonth,
  nextMonth,
}: DateFieldProps) {
  const auto = useId();
  const inputId = id ?? `${auto}-input`;
  const noteId = `${auto}-note`;
  const wide = useMediaQuery(SIDE_SHEET_QUERY);
  const [text, setText] = useState(formatDate(value));
  const [bad, setBad] = useState(false);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);

  // A new value from outside (a form reset, the picker) replaces what is shown.
  useEffect(() => {
    setText((shown) => (parseDate(shown) === value ? shown : formatDate(value)));
    setBad(false);
  }, [value]);

  const outside = (iso: string) =>
    (min !== undefined && iso < min) || (max !== undefined && iso > max);
  const commit = (next: string) => {
    const iso = parseDate(next);
    if (iso === null || outside(iso)) return false;
    onChange(iso);
    return true;
  };
  const shownError = error ?? (bad ? invalidText : undefined);
  const close = () => {
    setOpen(false);
    opener.current?.focus();
  };
  const picker = (
    <DatePicker
      label={label}
      value={value === '' ? null : value}
      onChange={(iso) => {
        onChange(iso);
        close();
      }}
      locale={locale}
      {...(min === undefined ? {} : { min })}
      {...(max === undefined ? {} : { max })}
      {...(previousMonth === undefined ? {} : { previousMonth })}
      {...(nextMonth === undefined ? {} : { nextMonth })}
    />
  );
  return (
    <div className={cx('k-field', shownError !== undefined && 'k-field--error', className)}>
      <label htmlFor={inputId} className="k-field__label k-t-label-sm">
        {label}
      </label>
      <div ref={box} className="k-field__box k-field__box--trail">
        <input
          id={inputId}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className="k-field__input k-t-body-lg"
          placeholder={placeholder}
          value={text}
          required={required}
          disabled={disabled}
          aria-invalid={shownError !== undefined || undefined}
          aria-describedby={
            shownError !== undefined || supporting !== undefined ? noteId : undefined
          }
          onChange={(e) => {
            setText(e.target.value);
            setBad(false);
            if (e.target.value === '' && !required) onChange('');
            else commit(e.target.value);
          }}
          onBlur={() => {
            if (text.trim() === '') return;
            if (!commit(text)) setBad(true);
          }}
        />
        <IconButton
          ref={opener}
          icon={iconEvent}
          label={openLabel}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen(!open)}
        />
      </div>
      {shownError !== undefined || supporting !== undefined ? (
        <p
          id={noteId}
          className="k-field__note k-t-label-sm"
          role={shownError === undefined ? undefined : 'alert'}
        >
          {shownError ?? supporting}
        </p>
      ) : null}
      {open && wide ? (
        <AnchoredPanel anchor={box} label={label} onClose={close}>
          {picker}
        </AnchoredPanel>
      ) : null}
      {!wide && open ? (
        <BottomSheet
          open={open}
          onClose={close}
          label={label}
          title={label}
          closeLabel={closeLabel}
        >
          {picker}
        </BottomSheet>
      ) : null}
    </div>
  );
}
